'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "style", "date", "skipDataCurrent", "ariaLabelFormat"];
import * as React from 'react';
import { createSelector, useStore } from '@base-ui/utils/store';
import { useRenderElement } from "../../base-ui-copy/utils/useRenderElement.mjs";
import { useCompositeListItem } from "../../base-ui-copy/composite/list/useCompositeListItem.mjs";
import { useCompositeListContext } from "../../base-ui-copy/composite/list/CompositeListContext.mjs";
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
import { useEventCalendarStoreContext } from "../../use-event-calendar-store-context/index.mjs";
import { getCalendarGridHeaderCellId } from "../../internals/utils/accessibility-utils.mjs";
import { getNavigationTarget } from "../../internals/utils/getNavigationTarget.mjs";
import { useCalendarGridRootContext } from "../root/CalendarGridRootContext.mjs";
import { schedulerNowSelectors } from "../../scheduler-selectors/index.mjs";
const selectorIsCurrentDate = createSelector((state, date, skipDataCurrent) => !skipDataCurrent && schedulerNowSelectors.isCurrentDay(state, date));
export const CalendarGridHeaderCell = /*#__PURE__*/React.forwardRef(function CalendarGridHeaderCell(componentProps, forwardedRef) {
  const adapter = useAdapterContext();
  const {
      // Rendering props

      // Internal props
      date,
      skipDataCurrent,
      ariaLabelFormat = adapter.formats.weekday
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);
  const store = useEventCalendarStoreContext();
  const {
    id: rootId,
    focusedCell,
    setFocusedCell,
    rowTypes,
    rowsPerType
  } = useCalendarGridRootContext();
  const isCurrentDay = useStore(store, selectorIsCurrentDate, date.value, skipDataCurrent);
  const {
    ref: listItemRef,
    index
  } = useCompositeListItem();
  const {
    elementsRef
  } = useCompositeListContext();
  const id = getCalendarGridHeaderCellId(rootId, index);
  const cellRef = React.useRef(null);
  const hasFocus = focusedCell?.rowType === 'header' && focusedCell?.rowIndex === 0 && focusedCell?.columnIndex === index;

  // Apply DOM focus when this cell becomes the focused cell
  React.useEffect(() => {
    if (hasFocus && cellRef.current && !cellRef.current.contains(document.activeElement)) {
      cellRef.current.focus({
        preventScroll: true
      });
    }
  }, [hasFocus]);
  const handleKeyDown = event => {
    const target = getNavigationTarget(event.key, 'header', 0, index, {
      columnCount: elementsRef.current.length,
      rowTypes,
      rowsPerType
    });
    if (target) {
      event.preventDefault();
      setFocusedCell(target);
      return;
    }
    if (event.key === 'Enter' && event.target === event.currentTarget) {
      const button = event.currentTarget.querySelector('button');
      if (button) {
        button.click();
      }
    }
  };
  const handleFocus = event => {
    if (event.target === event.currentTarget) {
      setFocusedCell({
        rowType: 'header',
        rowIndex: 0,
        columnIndex: index
      });
    }
  };
  const state = React.useMemo(() => ({
    current: isCurrentDay
  }), [isCurrentDay]);
  const keyboardProps = {
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus
  };
  return useRenderElement('div', componentProps, {
    state,
    ref: [forwardedRef, listItemRef, cellRef],
    props: [{
      role: 'columnheader',
      id,
      'aria-label': `${adapter.formatByString(date.value, ariaLabelFormat)}`,
      'aria-colindex': index + 1
    }, keyboardProps, elementProps]
  });
});
if (process.env.NODE_ENV !== "production") CalendarGridHeaderCell.displayName = "CalendarGridHeaderCell";